import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { X, Check } from 'lucide-react';
import { getImageAssetPath } from '@/lib/imageHelper';
import OptimizedImage from '@/components/common/OptimizedImage';

const TransformationSection = () => {
  const { t } = useTranslation();
  const profileImagePath = getImageAssetPath('ddi.jpeg'); // Pfad zum Profilbild
  
  return (
    <section id="transformation" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t('businessAppsPage.transformation.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('businessAppsPage.transformation.subtitle')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
          {/* Ohne individuelle Business-Anwendungen */}
          <div className="bg-red-50 rounded-xl overflow-hidden border border-red-100">
            <div className="bg-red-600 text-white p-4 text-center">
              <h3 className="text-xl font-bold">
                {t('businessAppsPage.transformation.without.title')}
              </h3>
            </div>
            <div className="p-6">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <X className="h-6 w-6 text-red-500 mr-4 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t('businessAppsPage.transformation.without.items.workarounds')}
                  </span>
                </li>
                <li className="flex items-start">
                  <X className="h-6 w-6 text-red-500 mr-4 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t('businessAppsPage.transformation.without.items.integration')}
                  </span>
                </li>
                <li className="flex items-start">
                  <X className="h-6 w-6 text-red-500 mr-4 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t('businessAppsPage.transformation.without.items.data')}
                  </span>
                </li>
                <li className="flex items-start">
                  <X className="h-6 w-6 text-red-500 mr-4 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t('businessAppsPage.transformation.without.items.adaptation')}
                  </span>
                </li>
                <li className="flex items-start">
                  <X className="h-6 w-6 text-red-500 mr-4 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t('businessAppsPage.transformation.without.items.costs')}
                  </span>
                </li>
                <li className="flex items-start">
                  <X className="h-6 w-6 text-red-500 mr-4 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t('businessAppsPage.transformation.without.items.noDataCollection')}
                  </span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Mit unseren maßgeschneiderten Lösungen */}
          <div className="bg-green-50 rounded-xl overflow-hidden border border-green-100 shadow-md">
            <div className="bg-green-600 text-white p-4 text-center">
              <h3 className="text-xl font-bold">
                {t('businessAppsPage.transformation.with.title')}
              </h3>
            </div>
            <div className="p-6">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Check className="h-6 w-6 text-green-500 mr-4 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t('businessAppsPage.transformation.with.items.fit')}
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-6 w-6 text-green-500 mr-4 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t('businessAppsPage.transformation.with.items.integration')}
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-6 w-6 text-green-500 mr-4 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t('businessAppsPage.transformation.with.items.data')}
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-6 w-6 text-green-500 mr-4 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t('businessAppsPage.transformation.with.items.scaling')}
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="h-6 w-6 text-green-500 mr-4 flex-shrink-0" />
                  <span className="text-gray-700">
                    {t('businessAppsPage.transformation.with.items.costs')}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Emotionales Zitat */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-blue-100 rounded-xl p-6 md:p-8 relative">
            <div className="text-6xl text-blue-300 font-serif absolute top-4 left-4">"</div>
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <div className="md:w-1/4 flex-shrink-0">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-white shadow-md">
                  <OptimizedImage 
                    src={profileImagePath} 
                    alt="Dennis Dittrich, splusd-solutions" 
                    width={128}
                    height={128}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="md:w-3/4">
                <blockquote className="relative z-10">
                  <p className="text-lg md:text-xl text-blue-900 italic mb-6">
                    "Software ist dafür da, deine bewährten Abläufe zu stärken – nicht, sie zu diktieren.
                    Unternehmen, die ihre Prozesse über Jahre erfolgreich verfeinert haben, brauchen keine Revolution – sie brauchen eine Software, die sich nahtlos anpasst, statt alles auf den Kopf zu stellen."
                  </p>
                  <footer className="text-blue-800 font-medium">
                    Dennis Dittrich, Geschäftsführer, splusd-solutions
                  </footer>
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransformationSection; 